/**
 * Rent or Buy Calculator
 * 렌트 vs 구매 계산기 - 호주 부동산
 */

import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Calculator, Home, Key, ArrowLeft } from "lucide-react";
import { useState, useEffect } from "react";
import { useLocation, useRoute } from "wouter";

export default function RentOrBuyCalculator() {
  const [, setLocation] = useLocation();
  const [match, params] = useRoute<{ price?: string; period?: string }>("/tools/rent-or-buy?:price?:period");

  // URL 파라미터에서 초기값 가져오기
  const urlParams = new URLSearchParams(window.location.search);
  const initialRentPrice = urlParams.get("price") ? parseInt(urlParams.get("price") || "0") / 100 : 0;
  const initialPeriod = urlParams.get("period") || "weekly";

  const [rentPrice, setRentPrice] = useState<number>(initialRentPrice || 0);
  const [rentPeriod, setRentPeriod] = useState<string>(initialPeriod || "weekly");
  const [propertyPrice, setPropertyPrice] = useState<number>(0);
  const [deposit, setDeposit] = useState<number>(20); // %
  const [interestRate, setInterestRate] = useState<number>(6.5); // %
  const [loanTerm, setLoanTerm] = useState<number>(30); // years
  const [annualRentIncrease, setAnnualRentIncrease] = useState<number>(3); // %
  const [propertyGrowth, setPropertyGrowth] = useState<number>(5); // %
  const [maintenance, setMaintenance] = useState<number>(1); // % of property value per year
  const [rates, setRates] = useState<number>(2000); // Council rates per year
  const [insurance, setInsurance] = useState<number>(1500); // Home insurance per year

  // 계산 결과
  const [results, setResults] = useState<{
    monthlyRent: number;
    monthlyMortgage: number;
    totalRentCost: number;
    totalBuyCost: number;
    breakEvenYears: number;
    savings: number;
  } | null>(null);

  // 주세를 월세로 변환
  const getMonthlyRent = () => {
    if (rentPeriod === "weekly") {
      return rentPrice * 52 / 12;
    } else if (rentPeriod === "monthly") {
      return rentPrice;
    }
    return rentPrice;
  };

  // 대출 계산
  const calculateMortgage = () => {
    if (!propertyPrice || propertyPrice <= 0) return 0;

    const loanAmount = propertyPrice * (1 - deposit / 100);
    const monthlyRate = interestRate / 100 / 12;
    const numPayments = loanTerm * 12;

    if (monthlyRate === 0) {
      return loanAmount / numPayments;
    }

    const monthlyPayment = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) /
                          (Math.pow(1 + monthlyRate, numPayments) - 1);

    return monthlyPayment;
  };

  // 10년간 총 비용 계산
  const calculateCosts = () => {
    const monthlyRent = getMonthlyRent();
    const monthlyMortgage = calculateMortgage();

    if (!propertyPrice || propertyPrice <= 0) {
      setResults(null);
      return;
    }

    const years = 10;
    let totalRentCost = 0;
    let currentRent = monthlyRent;

    // 렌트 비용 (10년간, 연간 증가율 적용)
    for (let year = 0; year < years; year++) {
      totalRentCost += currentRent * 12;
      currentRent *= (1 + annualRentIncrease / 100);
    }

    // 구매 비용 계산
    const loanAmount = propertyPrice * (1 - deposit / 100);
    const totalMortgagePayments = monthlyMortgage * 12 * years;
    const totalInterest = totalMortgagePayments - loanAmount;
    const depositAmount = propertyPrice * (deposit / 100);
    const totalMaintenance = (propertyPrice * maintenance / 100) * years;
    const totalRates = rates * years;
    const totalInsurance = insurance * years;

    const totalBuyCost = depositAmount + totalMortgagePayments + totalMaintenance + totalRates + totalInsurance;

    // 부동산 가치 증가
    const futurePropertyValue = propertyPrice * Math.pow(1 + propertyGrowth / 100, years);
    const equityGain = futurePropertyValue - propertyPrice;

    // 순 비용 (구매 비용 - 자산 가치 증가)
    const netBuyCost = totalBuyCost - equityGain;

    // Break-even 계산 (순 비용이 렌트 비용과 같아지는 시점)
    let breakEvenYears = years;
    for (let y = 1; y <= years; y++) {
      let rentCost = 0;
      let rent = monthlyRent;
      for (let i = 0; i < y; i++) {
        rentCost += rent * 12;
        rent *= (1 + annualRentIncrease / 100);
      }

      const buyLoanAmount = propertyPrice * (1 - deposit / 100);
      const buyMonthlyMortgage = calculateMortgage();
      const buyTotalPayments = buyMonthlyMortgage * 12 * y;
      const buyInterest = buyTotalPayments - buyLoanAmount;
      const buyDeposit = propertyPrice * (deposit / 100);
      const buyMaintenance = (propertyPrice * maintenance / 100) * y;
      const buyRates = rates * y;
      const buyInsurance = insurance * y;
      const buyTotalCost = buyDeposit + buyTotalPayments + buyMaintenance + buyRates + buyInsurance;
      const buyFutureValue = propertyPrice * Math.pow(1 + propertyGrowth / 100, y);
      const buyEquityGain = buyFutureValue - propertyPrice;
      const buyNetCost = buyTotalCost - buyEquityGain;

      if (buyNetCost <= rentCost) {
        breakEvenYears = y;
        break;
      }
    }

    setResults({
      monthlyRent,
      monthlyMortgage,
      totalRentCost,
      totalBuyCost: netBuyCost,
      breakEvenYears: breakEvenYears > years ? years : breakEvenYears,
      savings: totalRentCost - netBuyCost,
    });
  };

  useEffect(() => {
    if (rentPrice > 0 && propertyPrice > 0) {
      calculateCosts();
    }
  }, [rentPrice, rentPeriod, propertyPrice, deposit, interestRate, loanTerm, annualRentIncrease, propertyGrowth, maintenance, rates, insurance]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <PageContainer size="default">
      <div className="max-w-4xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => setLocation("/properties")}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          목록으로
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5" />
              Rent or Buy Calculator
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-2">
              Compare the costs of renting vs buying a property over 10 years
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* 렌트 정보 */}
            <div>
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Key className="w-4 h-4" />
                Rental Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="rent-price">Rent Price (AUD)</Label>
                  <Input
                    id="rent-price"
                    type="number"
                    value={rentPrice || ""}
                    onChange={(e) => setRentPrice(parseFloat(e.target.value) || 0)}
                    placeholder="800"
                  />
                </div>
                <div>
                  <Label htmlFor="rent-period">Period</Label>
                  <select
                    id="rent-period"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                    value={rentPeriod}
                    onChange={(e) => setRentPeriod(e.target.value)}
                  >
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="rent-increase">Annual Rent Increase (%)</Label>
                  <Input
                    id="rent-increase"
                    type="number"
                    step="0.1"
                    value={annualRentIncrease}
                    onChange={(e) => setAnnualRentIncrease(parseFloat(e.target.value) || 0)}
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* 구매 정보 */}
            <div>
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Home className="w-4 h-4" />
                Purchase Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="property-price">Property Price (AUD)</Label>
                  <Input
                    id="property-price"
                    type="number"
                    value={propertyPrice || ""}
                    onChange={(e) => setPropertyPrice(parseFloat(e.target.value) || 0)}
                    placeholder="800000"
                  />
                </div>
                <div>
                  <Label htmlFor="deposit">Deposit (%)</Label>
                  <Input
                    id="deposit"
                    type="number"
                    step="0.1"
                    value={deposit}
                    onChange={(e) => setDeposit(parseFloat(e.target.value) || 0)}
                  />
                </div>
                <div>
                  <Label htmlFor="interest-rate">Interest Rate (%)</Label>
                  <Input
                    id="interest-rate"
                    type="number"
                    step="0.1"
                    value={interestRate}
                    onChange={(e) => setInterestRate(parseFloat(e.target.value) || 0)}
                  />
                </div>
                <div>
                  <Label htmlFor="loan-term">Loan Term (years)</Label>
                  <Input
                    id="loan-term"
                    type="number"
                    value={loanTerm}
                    onChange={(e) => setLoanTerm(parseInt(e.target.value) || 30)}
                  />
                </div>
                <div>
                  <Label htmlFor="property-growth">Property Growth Rate (% per year)</Label>
                  <Input
                    id="property-growth"
                    type="number"
                    step="0.1"
                    value={propertyGrowth}
                    onChange={(e) => setPropertyGrowth(parseFloat(e.target.value) || 0)}
                  />
                </div>
                <div>
                  <Label htmlFor="maintenance">Maintenance (% of property value per year)</Label>
                  <Input
                    id="maintenance"
                    type="number"
                    step="0.1"
                    value={maintenance}
                    onChange={(e) => setMaintenance(parseFloat(e.target.value) || 0)}
                  />
                </div>
                <div>
                  <Label htmlFor="rates">Council Rates (per year)</Label>
                  <Input
                    id="rates"
                    type="number"
                    value={rates}
                    onChange={(e) => setRates(parseFloat(e.target.value) || 0)}
                  />
                </div>
                <div>
                  <Label htmlFor="insurance">Home Insurance (per year)</Label>
                  <Input
                    id="insurance"
                    type="number"
                    value={insurance}
                    onChange={(e) => setInsurance(parseFloat(e.target.value) || 0)}
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* 결과 */}
            {results && (
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">Results (10 Year Comparison)</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Renting</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Monthly Rent:</span>
                          <span className="font-semibold">{formatCurrency(results.monthlyRent)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Total Cost (10 years):</span>
                          <span className="font-bold text-lg">{formatCurrency(results.totalRentCost)}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Buying</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Monthly Mortgage:</span>
                          <span className="font-semibold">{formatCurrency(results.monthlyMortgage)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Net Cost (10 years):</span>
                          <span className="font-bold text-lg">{formatCurrency(results.totalBuyCost)}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <Card className={results.savings > 0 ? "bg-green-50 dark:bg-green-950 border-green-200" : "bg-red-50 dark:bg-red-950 border-red-200"}>
                  <CardContent className="p-6">
                    <div className="text-center space-y-2">
                      <p className="text-sm text-muted-foreground">
                        {results.savings > 0 ? "Buying is" : "Renting is"} more cost-effective
                      </p>
                      <p className={`text-2xl font-bold ${results.savings > 0 ? "text-green-600" : "text-red-600"}`}>
                        {formatCurrency(Math.abs(results.savings))}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {results.savings > 0 ? "saved by buying" : "saved by renting"} over 10 years
                      </p>
                      {results.breakEvenYears < 10 && (
                        <p className="text-xs text-muted-foreground mt-2">
                          Break-even point: ~{results.breakEvenYears} years
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground">
                    <strong>Note:</strong> This calculator is for illustrative purposes only.
                    Actual costs may vary based on individual circumstances, tax implications,
                    and market conditions. Please consult with a financial advisor before making
                    property decisions.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
}

